import { c as _c } from "react/compiler-runtime";
import type { DOMConversionMap, DOMConversionOutput, DOMExportOutput, EditorConfig, LexicalNode, SerializedLexicalNode, Spread, NodeKey } from 'lexical';
import { DecoratorNode, $applyNodeReplacement, $getNodeByKey } from 'lexical';
import * as React from 'react';
import { TextMatchTransformer } from '@lexical/markdown';
import { clamp, pick } from 'remeda';
import { createEl } from 'shared/document';
import { doubleQuote } from 'shared/text';
import { uri } from 'shared/uri';
import { compactObj } from 'shared/collection';
import { useHover } from 'shared/ui';
import { P, match } from 'ts-pattern';
import { ActionIcon, LoadingOverlay, useCombobox, Combobox as Combo, Button } from '@mantine/core';
import Img from 'next/image';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HiTrash } from 'react-icons/hi';
import { CSSProperties, JSX, useEffect, useState } from 'react';
import { useDrag, useLatest } from 'components/hooks';
import { StyleProps } from 'components';
import { IconImgAlign } from 'components/icons';
import { ImgURL } from 'components/MarkdownOptions';
import { env } from 'utils/env';
type ImageNodeCommon = {
  alt?: string;
  title?: string;
  width?: number;
  srcPlaceholder?: string;
  align?: Align;
};
type ImageNodeUploading = ImageNodeCommon & {
  state: 'uploading';
};
type ImageNodeUploaded = ImageNodeCommon & {
  state: 'uploaded' | 'external' | 'pasted';
  src: string;
};
export type ImageNodeProps = ImageNodeUploading | ImageNodeUploaded;
export type SerializedImageNode = Spread<{
  type: 'image';
  version: 1;
} & ImageNodeProps, SerializedLexicalNode>;
export const $createImageNode = (img: ImageNodeProps, key?: NodeKey) => $applyNodeReplacement(new ImageNode(img, key)) as ImageNode;
export const $isImageNode = (node: LexicalNode | null | undefined): node is ImageNode => node instanceof ImageNode;
export class ImageNode extends DecoratorNode<JSX.Element> {
  __img: ImageNodeProps;
  constructor(img: ImageNodeProps, key?: NodeKey) {
    super(key);
    this.__img = img;
  }
  static DefaultWidth = 600;
  static getType = () => 'image';
  static clone = (node: ImageNode) => $createImageNode({
    ...node.__img
  }, node.__key);
  static importJSON = ({
    type,
    version,
    ...img
  }: SerializedImageNode) => $createImageNode(img);
  static importDOM = (): DOMConversionMap | null => ({
    img: (node: Node) => ({
      conversion: (domNode: Node): null | DOMConversionOutput => match(domNode).with(P.instanceOf(HTMLImageElement), imgEl => {
        const {
          alt,
          src,
          title
        } = imgEl;
        const width = Math.min(imgEl.width, ImageNode.DefaultWidth);
        const node = $createImageNode({
          state: 'pasted',
          alt,
          src,
          title,
          width
        });
        return {
          node
        };
      }).otherwise(() => null),
      priority: 0
    })
  });
  exportJSON(): SerializedImageNode {
    return {
      ...this.__img,
      type: 'image',
      version: 1
    };
  }
  exportDOM = (): DOMExportOutput => ({
    element: createEl('img', {
      src: match(this.__img).with({
        state: 'uploading'
      }, i => i.srcPlaceholder).otherwise(i => i.src),
      ...pick(this.__img, ['alt', 'title'])
    })
  });
  createDOM = (config: EditorConfig): HTMLElement => createEl('span', {
    datatype: 'image',
    'data-type': 'ImageNode'
  });
  updateDOM = () => false;
  getImg = () => this.__img;
  decorate() {
    return <ImageComponent img={this.__img} nodeKey={this.__key} />;
  }
  updateImg(img: Partial<ImageNodeProps>) {
    const writable = this.getWritable();
    writable.__img = {
      ...writable.__img,
      ...img
    } as any;
  }
  md = () => match(this.__img).with({
    state: P.union('uploaded', 'external')
  }, ({
    alt,
    src,
    width,
    align,
    title,
    state
  }) => `\n![${alt ?? ''}](${ImgURL.merge(src, {
    width,
    align
  })}${title ? ' ' + doubleQuote(title) : ''})\n`).otherwise(() => '');
  toString = () => this.md();
}
const imgRegex = /!\[(.*?)\]\(([^"\s]+)(?:\s*"((?:[^"\\]|\\.)*?)")?\)/; //https://regex101.com/r/fRWNrD/1
export const ImageTransformer: TextMatchTransformer = {
  dependencies: [ImageNode],
  export: node => $isImageNode(node) ? node.md() : null,
  regExp: imgRegex,
  importRegExp: new RegExp(imgRegex.source + '$'),
  replace: (textNode, match) => {
    const [, alt, srcRaw, title] = match;
    if (!srcRaw) return;
    const {
      src,
      data: {
        width,
        align
      }
    } = ImgURL.parse(srcRaw);
    const isInternal = uri(src).root == uri(env.NEXT_PUBLIC_SUPABASE_URL!).root;
    const imageNode = $createImageNode({
      state: isInternal ? 'uploaded' : 'external',
      src,
      alt,
      title,
      width,
      align
    });
    textNode.replace(imageNode);
  },
  trigger: ')',
  type: 'text-match'
};
export const ImageComponent = ({
  img,
  nodeKey
}: {
  img: ImageNodeProps;
  nodeKey: NodeKey;
}) => {
  const [editor] = useLexicalComposerContext();
  const {
    alt,
    title,
    srcPlaceholder,
    state
  } = img;
  const width = img.width ?? ImageNode.DefaultWidth;
  const align = img.align ?? 'center';
  const editable = editor.isEditable();
  const [dragWidth, setDragWidth] = useState<number>();
  const displayWidth = dragWidth ?? width;
  const imgProps = compactObj({
    alt: alt ?? '',
    title,
    width: displayWidth,
    height: displayWidth,
    ...match(img).with({
      state: P.union('uploaded', 'external', 'pasted')
    }, ({
      src
    }) => ({
      src: src ?? srcPlaceholder,
      placeholder: srcPlaceholder
    })).otherwise(() => ({
      src: srcPlaceholder ?? ''
    }))
  });
  const hover = useHover({
    disabled: !editable
  });
  const editProps = {
    style: {
      visibility: hover.hovered ? undefined : 'hidden'
    } as Partial<CSSProperties>
  };
  const latest = useLatest({
    displayWidth,
    width,
    dragWidth,
    align
  });
  useEffect(() => {
    if (dragWidth && img.width == dragWidth) setDragWidth(undefined);
  }, [img.width]);
  const dragHandleProps = (side: 'left' | 'right'): Partial<Parameters<typeof DragHandle>[0]> => ({
    className: `absolute transform -translate-y-1/2 top-1/2`,
    onChange: ({
      delta: {
        x
      }
    }) => {
      const {
        width: width_0,
        align: align_0
      } = latest.current;
      setDragWidth(clamp(width_0 + (align_0 == 'center' ? 2 : 1) * (side == 'left' ? -x : +x), {
        min: 100,
        max: 2000
      }));
    },
    onEnd: () => {
      const {
        dragWidth: dragWidth_0
      } = latest.current;
      if (!dragWidth_0) return;
      editor.update(() => {
        const node = $getNodeByKey<ImageNode>(nodeKey);
        if (!node) return;
        node.updateImg({
          width: dragWidth_0
        });
      });
    },
    style: {
      ...editProps.style,
      ...(side == 'left' ? {
        left: 6
      } : {
        right: 6
      })
    }
  });
  return <div className='flex cursor-default py-1' style={{
    justifyContent: match(align).with('left', () => 'start').with('right', () => 'end').otherwise(() => 'center')
  }}>
      <div ref={hover.ref} className='relative'>
        <Img draggable={false} loader={({
        src: src_0,
        width: width_1
      }) => state == 'external' ? src_0 : uri(src_0).with({
        query: {
          width: width_1?.toString(),
          height: width_1?.toString(),
          resize: 'contain'
        }
      }).url} {...imgProps} />
        {editable && <>
            <LoadingOverlay visible={state == 'uploading'} overlayProps={{
          blur: 1
        }} />
            <div className='absolute top-1 right-1 flex items-center gap-2 justify-center bg-white/50 rounded-md px-2 shadow-md' {...editProps}>
              <ImageAlign align={img.align ?? 'center'} onChange={align_1 => editor.update(() => {
            const node_0 = $getNodeByKey(nodeKey) as ImageNode;
            if (!node_0) return;
            node_0.updateImg({
              align: align_1
            });
          })} />
              <ActionIcon onClick={() => {
            editor.update(() => {
              const node_1 = $getNodeByKey(nodeKey) as ImageNode;
              if (!node_1) return;
              const parent = node_1.getParent();
              if (parent?.getChildrenSize() == 1) parent.remove();else node_1.remove();
            });
          }}>
                <HiTrash />
              </ActionIcon>
            </div>
            <DragHandle {...dragHandleProps('left')} />
            <DragHandle {...dragHandleProps('right')} />
          </>}
      </div>
    </div>;
};
const alignOptions = ['left', 'center', 'right'] as const;
type Align = (typeof alignOptions)[number];
const ImageAlign = t0 => {
  const $ = _c(15);
  const {
    align,
    onChange
  } = t0;
  const combo = useCombobox();
  let t1;
  if ($[0] !== onChange) {
    t1 = v => {
      if (v) {
        onChange(v as Align);
      }
    };
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Combo.Chevron />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== combo) {
    t3 = () => combo.toggleDropdown();
    $[3] = combo;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== align) {
    t4 = <IconImgAlign align={align} />;
    $[5] = align;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <Combo.Target><Button variant="transparent" rightSection={t2} onClick={t3}>{t4}</Button></Combo.Target>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Combo.Dropdown><Combo.Options className="flex items-center">{alignOptions.map(_temp)}</Combo.Options></Combo.Dropdown>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== combo || $[12] !== t1 || $[13] !== t5) {
    t7 = <Combo store={combo} onOptionSubmit={t1}>{t5}{t6}</Combo>;
    $[11] = combo;
    $[12] = t1;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
const DragHandle = ({
  className,
  style,
  onChange,
  onEnd
}: Pick<Parameters<typeof useDrag>[0], 'onChange' | 'onEnd'> & StyleProps) => {
  const drag = useDrag({
    onChange,
    onEnd,
    normalize: false
  });
  return <div ref={drag.ref} className={`w-[6px] h-[30px] shadow-md  bg-white/50 hover:bg-white rounded-md cursor-ew-resize select-none ${className ?? ''}`} style={style} />;
};
function _temp(a) {
  return <Combo.Option key={a} value={a}><IconImgAlign align={a} /></Combo.Option>;
}